// Token Expiration Warning Component for MUU Platform
import React, { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  Snackbar,
  Box,
  Typography,
  LinearProgress,
} from '@mui/material';
import {
  Warning,
  Refresh,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';

interface TokenExpirationWarningProps {
  variant?: 'snackbar' | 'alert' | 'banner';
  warningThreshold?: number; // em minutos
  autoRefresh?: boolean;
}

/**
 * Componente para avisar sobre expiração iminente do token
 */
export const TokenExpirationWarning: React.FC<TokenExpirationWarningProps> = ({
  variant = 'snackbar',
  warningThreshold = 10, // 10 minutos por padrão
  autoRefresh = false
}) => {
  const { authState, refreshTokens } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeUntilExpiry, setTimeUntilExpiry] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (!authState.tokens || !authState.isAuthenticated) {
      setShowWarning(false);
      return;
    }

    const checkExpiration = () => {
      const currentTime = Date.now();
      const expiryTime = authState.tokens!.expiresAt;
      const timeLeft = expiryTime - currentTime;
      const warningThresholdMs = warningThreshold * 60 * 1000;

      setTimeUntilExpiry(timeLeft);

      if (timeLeft <= warningThresholdMs && timeLeft > 0) {
        setShowWarning(true);
        
        // Auto refresh se habilitado e restam menos de 2 minutos
        if (autoRefresh && timeLeft <= 2 * 60 * 1000 && !isRefreshing) {
          handleRefresh();
        }
      } else {
        setShowWarning(false);
      }
    };

    // Verificar imediatamente
    checkExpiration();

    // Verificar a cada 60 segundos para melhor performance
    const interval = setInterval(checkExpiration, 60 * 1000);
    return () => clearInterval(interval);
  }, [authState.tokens, authState.isAuthenticated, warningThreshold, autoRefresh, isRefreshing]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const success = await refreshTokens();
      if (success) {
        setShowWarning(false);
      }
    } catch (error) {
      console.error('Erro ao renovar token:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDismiss = () => {
    setShowWarning(false);
  };

  const formatTimeLeft = (ms: number): string => {
    const minutes = Math.floor(ms / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);
    
    if (minutes > 0) {
      return `${minutes} minuto${minutes !== 1 ? 's' : ''}`;
    }
    return `${seconds} segundo${seconds !== 1 ? 's' : ''}`;
  };

  const getProgressValue = (): number => {
    const warningThresholdMs = warningThreshold * 60 * 1000;
    return Math.max(0, Math.min(100, (timeUntilExpiry / warningThresholdMs) * 100));
  };

  if (!showWarning) {
    return null;
  }

  const warningContent = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
      <Warning color="warning" />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Sua sessão expirará em {formatTimeLeft(timeUntilExpiry)}
        </Typography>
        <LinearProgress 
          variant="determinate" 
          value={getProgressValue()} 
          color="warning"
          sx={{ mt: 1, height: 4, borderRadius: 2 }}
        />
      </Box>
      <Button
        variant="outlined"
        size="small"
        startIcon={isRefreshing ? undefined : <Refresh />}
        onClick={handleRefresh}
        disabled={isRefreshing}
        sx={{ minWidth: 120 }}
      >
        {isRefreshing ? 'Renovando...' : 'Renovar Sessão'}
      </Button>
    </Box>
  );

  if (variant === 'snackbar') {
    return (
      <Snackbar
        open={showWarning}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={handleDismiss}
        autoHideDuration={null} // Não esconder automaticamente
      >
        <Alert 
          severity="warning" 
          onClose={handleDismiss}
          sx={{ minWidth: 400 }}
        >
          {warningContent}
        </Alert>
      </Snackbar>
    );
  }

  if (variant === 'banner') {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          backgroundColor: 'warning.light',
          color: 'warning.contrastText',
          p: 2,
        }}
      >
        {warningContent}
      </Box>
    );
  }

  // Default: alert variant
  return (
    <Alert 
      severity="warning" 
      onClose={handleDismiss}
      sx={{ mb: 2 }}
    >
      {warningContent}
    </Alert>
  );
};

export default TokenExpirationWarning;